import React, { useEffect, useState, useRef } from 'react';
import { FadeIn } from './ui/FadeIn';
import { DropletsIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const blends = [{
  name: 'Blooming Tea',
  tag: 'TENDENCIA',
  description: 'El arte de la flor que se abre en el agua.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/1.webp?v=1772406880'
}, {
  name: 'Blue Matcha',
  tag: 'EXÓTICO',
  description: 'Crea lattes azules mágicos con Butterfly Pea.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/2.webp?v=1772406880'
}, {
  name: 'Amanecer Verde',
  tag: 'ENERGÍA',
  description: 'Té verde con jengibre y limón para foco total.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/3.webp?v=1772406879'
}, {
  name: 'Noche Serena',
  tag: 'RELAX',
  description: 'Lavanda y manzanilla para dormir profundo.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/4.webp?v=1772406881'
}, {
  name: 'Berry Mix',
  tag: 'FRUTAL',
  description: 'Explosión de frutos rojos ideal para Ice Tea.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/5.webp?v=1772406881'
}, {
  name: 'Chai de Autor',
  tag: 'PREMIUM',
  description: 'Especias exóticas y té negro intenso.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/6.webp?v=1772406880'
}, {
  name: 'Hibiscus Real',
  tag: 'VIBRANTE',
  description: 'Un rojo intenso lleno de antioxidantes.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/7.webp?v=1772406880'
}, {
  name: 'Jazmín Imperial',
  tag: 'CLÁSICO',
  description: 'El aroma floral más buscado y delicado.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/8.webp?v=1772406879'
}];
export function MechanismSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    // Calculate active slide index
    const children = Array.from(el.children) as HTMLElement[];
    if (children.length === 0) return;
    const scrollCenter = el.scrollLeft + el.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    children.forEach((child, i) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const distance = Math.abs(scrollCenter - childCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });
    setActiveIndex(closestIndex);
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
    };
  }, []);
  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('div')?.offsetWidth ?? 260;
    el.scrollBy({
      left: direction === 'left' ? -cardWidth - 16 : cardWidth + 16,
      behavior: 'smooth'
    });
  };
  const scrollToIndex = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    if (children[index]) {
      children[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  };
  return <section className="py-14 px-6 lg:px-8 bg-tea-green text-tea-cream relative overflow-hidden" data-id="element-195">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(#C9A84C 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-196"></div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-197">
        <div className="text-center mb-12" data-id="element-198">
          <FadeIn data-id="element-199">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-tea-gold/20 text-tea-gold mb-6" data-id="element-200">
              <DropletsIcon className="w-8 h-8" data-id="element-201" />
            </div>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-tea-gold mb-6" data-id="element-202">
              El Método de Alquimia Rentable
            </h2>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-203">
            <p className="text-lg md:text-xl text-tea-sage/90 max-w-3xl mx-auto leading-relaxed font-light" data-id="element-204">
              Hemos condensado el conocimiento de "El Negocio del Té" en un
              sistema de implementación rápida que utiliza tés de alto impacto
              visual (Matcha Azul, Blooming Teas y otros más) para generar
              ventas virales inmediatas, respaldado por una Calculadora de
              Rentabilidad que asegura tu ganancia desde el día 1.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.3} data-id="element-205">
          <div className="flex items-center justify-between mb-6" data-id="element-206">
            <p className="text-sm font-bold tracking-widest text-tea-gold uppercase" data-id="element-207">
              El Arte que vas a dominar (Sin experiencia previa)
            </p>
            {/* Navigation arrows */}
            <div className="flex items-center gap-2" data-id="element-208">
              <button onClick={() => scroll('left')} disabled={!canScrollLeft} className="w-10 h-10 rounded-full border border-tea-gold/40 flex items-center justify-center text-tea-gold transition-all duration-200 hover:bg-tea-gold/20 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Anterior" data-id="element-209">
                <ChevronLeftIcon className="w-5 h-5" data-id="element-210" />
              </button>
              <button onClick={() => scroll('right')} disabled={!canScrollRight} className="w-10 h-10 rounded-full border border-tea-gold/40 flex items-center justify-center text-tea-gold transition-all duration-200 hover:bg-tea-gold/20 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Siguiente" data-id="element-211">
                <ChevronRightIcon className="w-5 h-5" data-id="element-212" />
              </button>
            </div>
          </div>
        </FadeIn>

        {/* Carousel */}
        <FadeIn delay={0.4} data-id="element-213">
          <div className="relative" data-id="element-214">
            {/* Fade edges */}
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-tea-green to-transparent z-10 pointer-events-none" style={{
            opacity: canScrollLeft ? 1 : 0,
            transition: 'opacity 0.3s'
          }} data-id="element-215" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-tea-green to-transparent z-10 pointer-events-none" style={{
            opacity: canScrollRight ? 1 : 0,
            transition: 'opacity 0.3s'
          }} data-id="element-216" />

            <div ref={scrollRef} className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-217">
              {blends.map(blend => <div key={blend.name} className="relative rounded-2xl overflow-hidden aspect-[3/4] flex-shrink-0 w-[70vw] sm:w-[45vw] md:w-[calc(25%-12px)] snap-start group" data-id="element-218">
                  <img src={blend.image} alt={blend.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-219" />

                  {/* Dark gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10" data-id="element-220" />

                  {/* Tag */}
                  <div className="absolute top-3 right-3 z-20" data-id="element-221">
                    <span className="inline-block px-3 py-1 text-[10px] md:text-xs font-bold tracking-wider bg-white/90 text-tea-charcoal rounded-full" data-id="element-222">
                      {blend.tag}
                    </span>
                  </div>

                  {/* Text content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-20" data-id="element-223">
                    <h3 className="font-heading font-bold text-white text-lg md:text-xl leading-tight mb-1" data-id="element-224">
                      {blend.name}
                    </h3>
                    <p className="text-white/80 text-xs md:text-sm font-light leading-snug" data-id="element-225">
                      {blend.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Dot indicators for mobile */}
            <div className="flex justify-center gap-1.5 mt-4 md:hidden" data-id="element-226">
              {blends.map((_, i) => <div key={i} className={`rounded-full transition-all duration-300 ${activeIndex === i ? 'w-6 h-1.5 bg-tea-gold' : 'w-1.5 h-1.5 bg-tea-gold/30'}`} data-id="element-227" />)}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}